import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useAuth } from "src/context/AuthContext";
import { useLang, useT } from "src/i18n";
import { resolveErrorMessage } from "src/i18n/errorCopy";
import { consumeLinkedInCallbackState, LINKEDIN_CALLBACK_PATH } from "src/auth/linkedin";
import { captureError } from "src/utils/errorReporting";
import { reaffirmAcceptances } from "src/data/legalDocument";

/**
 * Page rendered at /oauth/linkedin/callback. LinkedIn redirects here with
 * ?code=…&state=… (success) or ?error=…&error_description=… (failure /
 * user cancelled). Validates state, calls loginWithLinkedIn, then records
 * the Käyttöehdot acceptance via reaffirmAcceptances — same
 * OAuth-consent-as-acceptance audit trail the inline social buttons run.
 *
 * State-mismatch path captures a Sentry breadcrumb with which of the four
 * checks failed (hasCode / hasStateFromUrl / hasExpectedState / hasVerifier
 * / statesMatch) so we can distinguish stale callbacks from tampering.
 */
export default function LinkedInCallback() {
  const navigate = useNavigate();
  const { loginWithLinkedIn } = useAuth();
  const { lang } = useLang();
  const t = useT();
  const [errorMsg, setErrorMsg] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    (async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get("code");
        const stateFromUrl = params.get("state");
        const err = params.get("error");
        const { state: expectedState, verifier } = consumeLinkedInCallbackState();

        if (err === "user_cancelled_login" || err === "user_cancelled_authorize_code") {
          navigate("/", { replace: true });
          return;
        }

        if (err) {
          setErrorMsg(
            params.get("error_description") || t("auth.linkedinLoginFailed")
          );
          return;
        }

        if (!code || !stateFromUrl || !expectedState || stateFromUrl !== expectedState || !verifier) {
          captureError(new Error("LinkedIn OAuth state mismatch"), {
            tags: { auth: "linkedin", op: "callback" },
            extra: {
              hasCode: !!code,
              hasStateFromUrl: !!stateFromUrl,
              hasExpectedState: !!expectedState,
              statesMatch: stateFromUrl === expectedState,
              hasVerifier: !!verifier,
            },
          });
          setErrorMsg(t("auth.linkedinLoginFailed"));
          return;
        }

        const redirectUri = window.location.origin + LINKEDIN_CALLBACK_PATH;
        const authData = await loginWithLinkedIn({
          code,
          codeVerifier: verifier,
          redirectUri,
        });

        // Same OAuth-acceptance audit trail Google/Microsoft/Apple buttons run;
        // reaffirmAcceptances reports its own failures and never blocks auth.
        reaffirmAcceptances(authData);

        navigate("/", { replace: true });
      } catch (caught) {
        captureError(caught, { tags: { auth: "linkedin", op: "callback" } });
        setErrorMsg(resolveErrorMessage(caught, { t, lang, fallbackKey: "auth.linkedinLoginFailed" }));
      }
    })();
  }, [loginWithLinkedIn, navigate, lang, t]);

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", px: 2 }}>
      <Stack spacing={2} sx={{ alignItems: "center" }}>
        {errorMsg ? (
          <>
            <Alert severity="error">{errorMsg}</Alert>
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", cursor: "pointer", textDecoration: "underline" }}
              onClick={() => navigate("/", { replace: true })}>
              {t("auth.linkedin.backToHome")}
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {t("auth.linkedin.finishing")}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );
}
